import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { runGated } from "@/lib/adsGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sparkles, Upload, Loader2, ThumbsUp, HelpCircle, ThumbsDown,
  Check, X, ScanSearch, ChevronLeft,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { isNative, takeNativePhoto, dataUrlToFile } from "@/lib/native";

const VERDICT_META = {
  buy:   { label: "Buy",   Icon: ThumbsUp,   tone: "text-emerald-500 border-emerald-500/50 bg-emerald-500/10" },
  maybe: { label: "Maybe", Icon: HelpCircle, tone: "text-amber-500 border-amber-500/50 bg-amber-500/10" },
  leave: { label: "Leave", Icon: ThumbsDown, tone: "text-rose-500 border-rose-500/50 bg-rose-500/10" },
};

const MATCH_TONE = {
  strong: "text-emerald-500",
  okay:   "text-amber-500",
  weak:   "text-rose-500",
};

export default function OutfitJudge() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [price, setPrice] = useState("");
  const [context, setContext] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [verdict, setVerdict] = useState(null);
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    api.get("/outfit-judge/history?limit=6").then(({ data }) => setHistory(data)).catch(() => {});
    return () => { if (progressRef.current) clearInterval(progressRef.current); };
  }, []);

  const pickPhoto = async () => {
    if (isNative()) {
      try {
        const dataUrl = await takeNativePhoto();
        if (!dataUrl) return;
        const f = dataUrlToFile(dataUrl, `item-${Date.now()}.jpg`);
        if (f) { setFile(f); setPreview(URL.createObjectURL(f)); return; }
      } catch { /* fall through */ }
    }
    inputRef.current?.click();
  };

  const startProgress = () => {
    setProgress(0);
    const start = Date.now();
    const cap = 88;
    const duration = 20000;
    if (progressRef.current) clearInterval(progressRef.current);
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 2);
      setProgress(Math.min(cap, Math.round(eased * cap)));
      if (elapsed >= duration) {
        clearInterval(progressRef.current);
        progressRef.current = null;
      }
    }, 200);
  };
  const stopProgress = (final = 100) => {
    if (progressRef.current) { clearInterval(progressRef.current); progressRef.current = null; }
    setProgress(final);
  };

  const judge = async () => {
    if (!file) { toast.error("Add an item photo first."); return; }
    setBusy(true);
    setVerdict(null);
    startProgress();
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (price) fd.append("price", price);
      if (context) fd.append("context", context);
      const { data } = await runGated("outfit_judge", () =>
        api.post("/outfit-judge", fd, { headers: { "Content-Type": "multipart/form-data" } })
      );
      stopProgress(100);
      setVerdict(data);
      setHistory((h) => [data, ...h].slice(0, 6));
    } catch (e) {
      stopProgress(0);
      toast.error(e?.response?.data?.detail || "Couldn't judge this one — try again.");
    } finally {
      setTimeout(() => setBusy(false), 350);
    }
  };

  const reset = () => { setFile(null); setPreview(null); setVerdict(null); setPrice(""); setContext(""); };

  const meta = verdict ? VERDICT_META[verdict.verdict] || VERDICT_META.maybe : null;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 lg:py-14">
      <Link to="/tools" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground">
        <ChevronLeft size={13} /> Tools
      </Link>
      <div className="mt-4 flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <p className="eyebrow flex items-center gap-2"><ScanSearch size={12} /> AI · Pre-purchase check</p>
          <h1 className="editorial-h1 mt-3">Buy it? Maybe. Leave it?</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-lg">
            Snap the piece before you tap Add to Cart. We score it against your Style DNA and give you the honest answer in seconds.
          </p>
        </div>
      </div>

      <div className="mt-10 grid lg:grid-cols-12 gap-6">
        {/* LEFT — upload + inputs */}
        <div className="lg:col-span-5 space-y-4">
          <div className="border border-border p-6" data-testid="outfit-judge-upload">
            <p className="eyebrow">Step 1 · Item photo</p>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              hidden
              data-testid="outfit-judge-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) { setFile(f); setPreview(URL.createObjectURL(f)); }
              }}
            />
            <button
              type="button"
              onClick={pickPhoto}
              className="mt-4 w-full aspect-[4/5] border border-dashed border-border hover:border-accent hover:bg-accent/5 transition flex items-center justify-center overflow-hidden"
              data-testid="outfit-judge-pick"
            >
              {preview ? (
                <img src={preview} alt="Item" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center px-6">
                  <Upload size={22} className="mx-auto opacity-60" />
                  <p className="mt-3 eyebrow">Tap to add photo</p>
                  <p className="mt-1 text-[10px] text-muted-foreground">JPG or PNG · well-lit · full item in frame</p>
                </div>
              )}
            </button>
            {file && (
              <button
                type="button"
                onClick={reset}
                className="mt-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                data-testid="outfit-judge-clear"
              >
                <X size={11} /> Clear
              </button>
            )}
          </div>

          <div className="border border-border p-6">
            <p className="eyebrow">Step 2 · Context <span className="text-muted-foreground normal-case tracking-normal text-[10px] ml-1">(optional)</span></p>
            <div className="mt-4 grid gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Price</label>
                <Input
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="e.g. ₹2,499"
                  className="rounded-none mt-1"
                  data-testid="outfit-judge-price"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Occasion / notes</label>
                <Input
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder="e.g. Cousin's wedding, beach"
                  className="rounded-none mt-1"
                  data-testid="outfit-judge-context"
                />
              </div>
            </div>
          </div>

          <Button
            onClick={judge}
            disabled={busy || !file}
            data-testid="outfit-judge-run"
            className="w-full h-12 rounded-none uppercase tracking-[0.2em] text-xs"
          >
            {busy
              ? <><Loader2 size={14} className="animate-spin mr-2" />Judging</>
              : <><Sparkles size={14} className="mr-2" />Judge this item</>}
          </Button>
        </div>

        {/* RIGHT — verdict */}
        <div className="lg:col-span-7">
          <div className="border border-border p-6 lg:p-8 min-h-[420px]" data-testid="outfit-judge-result">
            {!verdict && !busy && (
              <div className="h-full min-h-[380px] flex flex-col items-center justify-center text-center">
                <ScanSearch size={30} className="opacity-40" strokeWidth={1.4} />
                <p className="mt-4 eyebrow">Awaiting judgment</p>
                <p className="mt-2 text-xs text-muted-foreground max-w-xs">
                  Upload a photo on the left. We&apos;ll score fit, colour, styling, and value in ~15 seconds.
                </p>
              </div>
            )}

            {busy && (
              <div className="h-full min-h-[380px] flex flex-col items-center justify-center px-8">
                <Loader2 size={30} className="animate-spin text-accent" />
                <div className="mt-6 w-full max-w-sm">
                  <div className="h-1 w-full bg-foreground/10 overflow-hidden">
                    <div
                      className="h-full bg-accent transition-[width] duration-300 ease-out"
                      style={{ width: `${progress}%` }}
                      data-testid="outfit-judge-progress"
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    <span>Analysing against your Style DNA</span>
                    <span>{progress}%</span>
                  </div>
                </div>
              </div>
            )}

            {verdict && !busy && (
              <div data-testid="outfit-judge-verdict" data-verdict={verdict.verdict}>
                <div className={`inline-flex items-center gap-2 px-4 py-2 border ${meta.tone}`}>
                  <meta.Icon size={16} />
                  <span className="text-xs uppercase tracking-[0.22em] font-semibold">{meta.label}</span>
                  <span className="ml-2 text-[10px] uppercase tracking-[0.18em] opacity-70">
                    Score {verdict.score}/10
                  </span>
                </div>
                {verdict.headline && (
                  <p className="font-serif text-2xl lg:text-3xl mt-5 leading-snug">
                    {verdict.headline}
                  </p>
                )}
                {verdict.reasoning && (
                  <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{verdict.reasoning}</p>
                )}

                <div className="mt-6 grid grid-cols-2 gap-4 text-xs">
                  <MatchStat label="Colour match" value={verdict.color_match} />
                  <MatchStat label="Silhouette match" value={verdict.silhouette_match} />
                </div>

                {(verdict.pros?.length || verdict.cons?.length) ? (
                  <div className="mt-6 grid sm:grid-cols-2 gap-6">
                    {verdict.pros?.length ? (
                      <div>
                        <p className="eyebrow text-emerald-500">Pros</p>
                        <ul className="mt-2 space-y-1.5">
                          {verdict.pros.map((p, i) => (
                            <li key={i} className="text-sm flex gap-2"><Check size={14} className="mt-0.5 text-emerald-500 shrink-0" />{p}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {verdict.cons?.length ? (
                      <div>
                        <p className="eyebrow text-rose-500">Cons</p>
                        <ul className="mt-2 space-y-1.5">
                          {verdict.cons.map((c, i) => (
                            <li key={i} className="text-sm flex gap-2"><X size={14} className="mt-0.5 text-rose-500 shrink-0" />{c}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {verdict.styling_tips?.length ? (
                  <div className="mt-6">
                    <p className="eyebrow">Style it with</p>
                    <ul className="mt-2 flex flex-wrap gap-2">
                      {verdict.styling_tips.map((t, i) => (
                        <li key={i} className="text-[11px] px-3 py-1.5 border border-border">{t}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {verdict.occasions?.length ? (
                  <div className="mt-5">
                    <p className="eyebrow">Wear it to</p>
                    <p className="mt-1.5 text-sm text-muted-foreground">{verdict.occasions.join(" · ")}</p>
                  </div>
                ) : null}

                {verdict.similar_but_better ? (
                  <div className="mt-6 border-t border-border pt-5">
                    <p className="eyebrow">Consider instead</p>
                    <p className="mt-1.5 text-sm">{verdict.similar_but_better}</p>
                  </div>
                ) : null}

                <div className="mt-8 flex gap-2">
                  <Button
                    onClick={reset}
                    variant="outline"
                    className="rounded-none uppercase tracking-[0.2em] text-[11px] h-10"
                    data-testid="outfit-judge-reset"
                  >
                    Judge another
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {history.length > 0 && (
        <section className="mt-16">
          <p className="eyebrow">Recent judgments</p>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {history.map((h) => {
              const m = VERDICT_META[h.verdict] || VERDICT_META.maybe;
              return (
                <div key={h.id} className="border border-border p-3" data-testid={`outfit-judge-history-${h.id}`}>
                  <div className={`inline-flex items-center gap-1.5 px-2 py-1 border ${m.tone}`}>
                    <m.Icon size={11} />
                    <span className="text-[10px] uppercase tracking-[0.18em] font-semibold">{m.label}</span>
                  </div>
                  <p className="mt-2 text-xs leading-snug line-clamp-3">{h.headline || "—"}</p>
                  {h.price && <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{h.price}</p>}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

function MatchStat({ label, value }) {
  return (
    <div className="border border-border p-3">
      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className={`mt-1 text-sm font-semibold capitalize ${MATCH_TONE[value] || ""}`}>{value || "—"}</p>
    </div>
  );
}
